import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { SessionService } from '../../session-service.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfigSettingDto } from '../../model/business/ConfigSettingDto';
import { TemplateService } from '../../services/TemplateService';
import { SessionGgService } from '../../services/session-gg.service';
import { Subscription } from 'rxjs';
import { SessionOrvService } from '../../services/session-orv.service';
import { ProductTypeEnum } from '../../model/enums/ProductTypeEnum';

@Component({
  selector: 'app-premium-block',
  templateUrl: './application-premium-block.component.html',
  styleUrls: ['./application-premium-block.component.css'],
})
export class PremiumBlockComponent implements OnInit, OnDestroy {
  @Output()
  submitEvent = new EventEmitter<void>();
  @Input()
  isStatementsAgreed: boolean;
  @Input()
  isDiscountTextShown = false;
  @Input()
  checkIsPremiumsCalculated: boolean;
  @Input()
  showProductDetails: boolean | undefined;
  @Input()
  showControlsBlock: boolean | undefined;
  @Output()
  downloadQuote = new EventEmitter<void>();
  @Input()
  productType: ProductTypeEnum;
  @Input()
  isMobile = false;
  @Input()
  isShowCalculatePremiumText = false;

  math = Math;
  policyTerms: string;
  intermediaryName: string;
  intermediaryFee: string;
  name: string;
  secondInsuredName: string;
  birthDate: Date;
  insuredAmount: number;
  product: string;
  termOfProduct: number;
  typeOfBenefit: string;
  isSecondInsured: boolean;
  secondInsuredBirthDate: Date;
  secondInsuredInsuredAmount: number;
  startDate: Date;
  endDate: Date;
  monthlyPremium: number;
  closingText: string;
  occupation: string;
  claimDuration: string;
  waitingPeriod: string;
  intermediaryFeePeriod: string;
  mentalIllness: string;
  indexationUsage: string;
  premiumSubscription: Subscription;
  annuityRate: number;
  orvSubscription: Subscription;
  isReceiptOpen = false;
  title: string;
  showIntermediaryFee: boolean;
  showIntermediarySubscriptionFee: boolean;
  intermediarySubscriptionFeePeriod: string;
  intermediarySubscriptionFee: string;
  intermediarySubscriptionFeeText: string;
  isOwnLabour: string = 'YES';

  constructor(
    private sessionService: SessionService,
    private translateService: TranslateService,
    private templateService: TemplateService,
    private sessionGgService: SessionGgService,
    private sessionOrvService: SessionOrvService,
    private translationService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.updatePremiumValues();
    if (this.isORVProduct) {
      this.subscribeToOrvFieldChanges();
    }
    this.setTitle();
    this.intermediarySubscriptionFeePeriod = this.formattedIntermediaryMediationSubscriptionFeePeriod();
  }

  ngOnDestroy(): void {
    this.premiumSubscription.unsubscribe();
    if (this.isORVProduct) {
      this.orvSubscription.unsubscribe();
    }
  }

  setTitle(): void {
    this.title = this.isORVProduct ? 'ClosingStatement.TitleORV' : 'ClosingStatement.TitleGG';
  }

  switchReceiptMenu(): void {
    this.isReceiptOpen = !this.isReceiptOpen;
  }

  get configSettings(): ConfigSettingDto {
    return this.sessionService.getConfigSettingsDto();
  }

  parseTemplatedStatement(statement: string, template: 'policy_terms' | 'name_intermediary' | 'intermediary_fee_period'): string {
    return this.templateService.parseTemplatedStatement(statement, template, {
      policyTerms: this.policyTerms,
      intermediaryName: this.intermediaryName,
    });
  }

  submitApplication(): void {
    this.submitEvent.emit();
  }

  handleDownloadQuote(): void {
    this.downloadQuote.emit();
  }

  initComponent(): void {
    this.policyTerms = this.sessionService.policyTerms;
    this.intermediaryName = this.sessionService.intermediaryName;
    this.intermediaryFee = this.sessionService.intermediaryFee;
    this.showIntermediaryFee = this.sessionService.showIntermediaryFee;
    this.showIntermediarySubscriptionFee = this.sessionService.showIntermediarySubscriptionFee;
    this.intermediarySubscriptionFee = this.sessionService.intermediarySubscriptionFee;
    this.intermediarySubscriptionFeeText = this.sessionService.intermediarySubscriptionFeeText;

    this.birthDate = this.sessionService.birthDate;
    this.insuredAmount = this.sessionService.insuredAmount;
    this.product = this.sessionService.product;
    this.termOfProduct = this.sessionService.termOfProduct;
    this.typeOfBenefit = this.translateService.instant(
      'CoverTypeEnum.' + this.sessionService.typeOfBenefit
    );

    this.isSecondInsured = this.sessionService.isSecondInsured;
    this.secondInsuredBirthDate = this.sessionService.secondInsuredBirthDate;
    this.secondInsuredInsuredAmount = this.sessionService.secondInsuredInsuredAmount;

    this.startDate = this.sessionService.startDate;
    this.endDate = this.sessionService.endDate;
    this.closingText = this.sessionService.closingText;
    this.claimDuration = this.formattedClaimDuration();
    this.annuityRate = this.sessionService.getCallculationRequest().annuityRate;
  }

  initGGFields(): void {
    this.occupation = this.sessionGgService.getOccupation();
    this.waitingPeriod = this.translationService.instant(`WaitingPeriodEnum.${this.sessionGgService.getWaitingPeriod()}`);
    this.mentalIllness = this.sessionGgService.getMentalIllness();
    this.indexationUsage = this.sessionGgService.getIndexationUsage();
    this.intermediaryFeePeriod = this.formattedIntermediaryMediationFeePeriod();
  }

  initORVFields(): void {
    this.name = this.sessionOrvService.getInsuredName();
    this.secondInsuredName = this.sessionOrvService.getInsured2Name();
  }

  formattedClaimDuration(): string {
    const duration = this.sessionGgService.getClaimDuration();
    const insuredOccupation = this.sessionGgService.getInsuredOccupation();
    this.isOwnLabour = insuredOccupation.ownLabour;
    if (duration === 'INSURANCE_END_DATE') {
      return this.templateService
        .parseTemplatedStatement('DurationEnum.OCCUPATION_YEARS_MAX', 'years', {years: insuredOccupation.maxEndAgeOwnLabour});
    }
    return this.translationService.instant(`DurationEnum.${duration}`);
  }

  formattedIntermediaryMediationFeePeriod(): string {
    const intermediaryFeePeriod = this.sessionService.getConfigSettingsDto().intermediaryFeePeriod;
    return this.templateService.parseTemplatedStatement('', 'intermediary_fee_period', {
      intermediaryFeePeriod
    });
  }

  formattedIntermediaryMediationSubscriptionFeePeriod(): string {
    const intermediarySubscriptionFeePeriod = this.sessionService.getConfigSettingsDto().intermediarySubscriptionFeePeriod;
    const text = this.templateService.parseTemplatedStatement('', 'intermediary_subscription_fee_period', {
      intermediarySubscriptionFeePeriod
    });
    return this.sessionService.getConfigSettingsDto().intermediarySubscriptionFeeText ? text.toLowerCase() : text;
  }

  get isGGProduct(): boolean {
    return this.productType === ProductTypeEnum.GG;
  }

  get isORVProduct(): boolean {
    return this.productType === ProductTypeEnum.ORV;
  }

  get showControls(): boolean {
    return !!this.showControlsBlock;
  }

  updatePremiumValues(): void {
    this.premiumSubscription = this.sessionService.getCalculationResponse.subscribe((value) => {
      try {
        this.monthlyPremium = value.premium;
        this.initComponent();
        this.initGGFields();
      } catch (e) {
        console.log(`GG Product. Error during update premium block: ${e}`);
      }
    });
  }

  subscribeToOrvFieldChanges(): void {
    this.orvSubscription = this.sessionOrvService.applicationFormValues.subscribe(() => {
      try {
        this.initORVFields();
      } catch (e) {
        console.log(`ORV product. Error during update premium block: ${e}`);
      }
    });
  }
}
